/**
 * PreferenciasOverrides — Threshold critical-ack + dedupe window por source.
 *
 * Spec docs/notificacoes/IMPLEMENTATION_SPEC_V1V2.md §3.4 #4 / §3.8 item 4.
 *
 * V2 — endpoint de write ainda nao implementado. Esta versao mostra
 * defaults read-only por source critica + Alert indicando que configuracao
 * sera via API.
 */
import { Stack, Group, ThemeIcon, Text, Table, Badge, ScrollArea, Box } from '@mantine/core'
import { IconAlertOctagon } from '@tabler/icons-react'
import { Callout, WidgetCard } from '@/components/ui'
import { sourceLabel, type PreferenceSource } from './preferencias-constants'

interface OverrideRow {
  // Tipado pela lista da tela: fonte renomeada ou removida quebra o build aqui
  // (TASK-24588 — sobrou 'gmud' depois que a fonte virou gmud_lifecycle).
  source: PreferenceSource
  ack_threshold_ms: number
  dedupe_window_min: number
  notes: string
}

/** Defaults pra source criticas (alinhados com spec §1.6 / §2.4). */
export const OVERRIDE_DEFAULTS: OverrideRow[] = [
  {
    source: 'cloudwatch_alarm',
    ack_threshold_ms: 5 * 60 * 1000,
    dedupe_window_min: 10,
    notes: 'Critical alarms exigem ack em 5min',
  },
  {
    source: 'guardduty',
    ack_threshold_ms: 15 * 60 * 1000,
    dedupe_window_min: 30,
    notes: 'Findings de segurança AWS',
  },
  {
    source: 'securityhub',
    ack_threshold_ms: 15 * 60 * 1000,
    dedupe_window_min: 30,
    notes: 'Compliance + CVE alerts',
  },
  {
    source: 'zabbix',
    ack_threshold_ms: 10 * 60 * 1000,
    dedupe_window_min: 5,
    notes: 'Triggers infra (host down, disk full)',
  },
  {
    source: 'ztna',
    ack_threshold_ms: 15 * 60 * 1000,
    dedupe_window_min: 60,
    notes: 'Eventos broker (sessão, deny rules)',
  },
  {
    source: 'gmud_lifecycle',
    ack_threshold_ms: 30 * 60 * 1000,
    dedupe_window_min: 0,
    notes: 'Aprovações de mudança (sem dedupe)',
  },
]

function formatMs(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`
  const min = Math.round(ms / 60_000)
  if (min < 60) return `${min}min`
  const h = Math.round(min / 60)
  return `${h}h`
}

export function PreferenciasOverrides() {
  return (
    <WidgetCard p="md">
      <Stack gap="sm">
        <Group gap={8} align="center">
          <ThemeIcon size={32} radius="md" color="orange" variant="light">
            <IconAlertOctagon size={18} />
          </ThemeIcon>
          <Text fw={600} size="sm">Overrides por fonte critica</Text>
        </Group>

        <Callout variant="info" title="V2 — configuração via API">
          <Text size="xs">
            Thresholds de acknowledge e janelas de dedupe por source critica.
            A edição via UI sera disponibilizada em uma próxima sprint;
            por enquanto, ajustes são feitos pelo time de plataforma
            (<code>admin/notifications/runbooks</code>).
          </Text>
        </Callout>

        <ScrollArea>
          <Box style={{ minWidth: 600 }}>
            <Table verticalSpacing="xs" horizontalSpacing="sm" striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Fonte</Table.Th>
                  <Table.Th>Ack threshold</Table.Th>
                  <Table.Th>Dedupe window</Table.Th>
                  <Table.Th>Observações</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {OVERRIDE_DEFAULTS.map((row) => (
                  <Table.Tr key={row.source}>
                    <Table.Td>
                      <Text size="sm">{sourceLabel(row.source)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color="red">
                        {formatMs(row.ack_threshold_ms)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color="gray">
                        {row.dedupe_window_min}min
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">{row.notes}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        </ScrollArea>
      </Stack>
    </WidgetCard>
  )
}
